/** Who is carrying how much in this sprint, against what they can carry.
 *
 * Brian, 2026-10-07: "some way to show the max resource points we should assign to a
 * resource". Each person's points in the sprint — an unpointed item counts as 1, as it
 * does in every rollup — against their capacity: 16 a fortnight unless they have their own
 * number. Over capacity is red and says by how much, because "Colbie: 22" means nothing
 * until it is put beside 16.
 *
 * A person's load counts everything they hold in the sprint, whatever team's board is on
 * screen: capacity belongs to the person, not to the filter.
 */

import { useState } from 'react'
import { Pencil } from 'lucide-react'

import { Button, Can, inputClass } from '@/components/ui'
import { Permissions } from '@/lib/session'
import { usePeople, useSetCapacity, type Person } from '@/features/people/usePeople'
import type { WorkItem } from '@/features/workbench/useWork'

const weight = (item: WorkItem) => item.points ?? 1

function CapacityEditor({ person, onDone }: { person: Person; onDone: () => void }) {
  const set = useSetCapacity()
  const [value, setValue] = useState(person.capacityIsDefault ? '' : String(person.sprintCapacity))
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault()
        const points = value.trim() === '' ? null : Number(value)
        if (points !== null && (Number.isNaN(points) || points < 0)) return
        set.mutate({ userId: person.id, points }, { onSuccess: onDone })
      }}
    >
      <input
        className={`${inputClass} !w-16 py-0.5 text-xs`}
        inputMode="numeric"
        placeholder="16"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        aria-label={`Sprint capacity for ${person.displayName}`}
        title="Points a fortnight. Leave blank for the default (16)."
      />
      <Button type="submit" className="px-2 py-0.5 text-xs" disabled={set.isPending}>
        Save
      </Button>
    </form>
  )
}

export function SprintLoad({ items, visibleAssignees }: { items: WorkItem[]; visibleAssignees: Set<string> }) {
  const { data: people } = usePeople()
  const [editing, setEditing] = useState<string | null>(null)
  const directory = new Map((people ?? []).map((p) => [p.id, p]))

  const load = new Map<string, number>()
  let unassigned = 0
  for (const item of items) {
    if (item.itemType === 'tracker') continue
    if (!item.assigneeUserId) {
      unassigned += weight(item)
      continue
    }
    load.set(item.assigneeUserId, (load.get(item.assigneeUserId) ?? 0) + weight(item))
  }
  const rows = [...load.entries()]
    .filter(([userId]) => visibleAssignees.has(userId))
    .map(([userId, points]) => ({ person: directory.get(userId), userId, points }))
    .sort((a, b) => b.points - a.points)

  if (rows.length === 0 && unassigned === 0) return null

  return (
    <section className="card mb-4 p-4" aria-label="Load against capacity">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="micro-label text-fg-muted">Load against capacity — points this sprint</p>
        {unassigned ? (
          <span className="text-xs text-fg-muted">
            <b className="text-fg">{unassigned}</b> point{unassigned === 1 ? '' : 's'} not assigned to anyone
          </span>
        ) : null}
      </div>
      <ul className="grid gap-x-6 gap-y-2.5 md:grid-cols-2 xl:grid-cols-3">
        {rows.map(({ person, userId, points }) => {
          const capacity = person?.sprintCapacity ?? 16
          const over = points - capacity
          const tone = over > 0 ? 'var(--status-red)' : points >= capacity * 0.85 ? 'var(--status-amber)' : 'var(--status-green)'
          const share = capacity > 0 ? Math.min(1, points / capacity) : 1
          return (
            <li key={userId} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="truncate text-fg">{person?.displayName ?? 'Someone no longer listed'}</span>
                {editing === userId && person ? (
                  <CapacityEditor person={person} onDone={() => setEditing(null)} />
                ) : (
                  <span className="flex shrink-0 items-center gap-1.5 text-xs">
                    <b className="text-fg">{points}</b>
                    <span className="text-fg-muted">
                      / {capacity}
                      {person?.capacityIsDefault ? '' : ' (own)'}
                    </span>
                    {over > 0 ? (
                      <span className="font-semibold" style={{ color: 'var(--status-red)' }}>
                        over by {over}
                      </span>
                    ) : null}
                    {person ? (
                      <Can permission={Permissions.projectsCreate}>
                        <button
                          type="button"
                          onClick={() => setEditing(userId)}
                          className="text-fg-muted hover:text-fg"
                          aria-label={`Change ${person.displayName}'s capacity`}
                          title="Change this person's capacity"
                        >
                          <Pencil size={11} aria-hidden />
                        </button>
                      </Can>
                    ) : null}
                  </span>
                )}
              </div>
              <div
                className="h-1.5 overflow-hidden rounded-pill"
                style={{ backgroundColor: 'color-mix(in srgb, var(--fg) 10%, transparent)' }}
                role="img"
                aria-label={`${points} of ${capacity} points`}
              >
                <div className="h-full rounded-pill" style={{ width: `${share * 100}%`, backgroundColor: tone }} />
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
